'use client';

import { useState, useRef } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { FileOutput, Upload, RotateCcw, ArrowRight, Download, FileText, AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { ToolSignupWall } from './tool-signup-wall';
import { extractInvoice, type ExtractedInvoice } from '@/lib/tools/invoice-extract';

interface PdfToUblConverterProps {
  locale: Locale;
  content: ToolContent;
}

export function PdfToUblConverter({ locale, content }: PdfToUblConverterProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [extracted, setExtracted] = useState<ExtractedInvoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const labels = content.labels;
  const errors = content.errors ?? {};

  const handleFileUpload = async (uploadedFile: File) => {
    if (!uploadedFile.name.toLowerCase().endsWith('.pdf')) {
      setError(errors.invalidPdf ?? 'Invalid PDF file');
      return;
    }

    if (uploadedFile.size > 10 * 1024 * 1024) {
      setError(errors.fileTooLarge ?? 'File is too large (max 10MB)');
      return;
    }

    setFile(uploadedFile);
    setError(null);
    setResult(null);
    setExtracted(null);
    setIsProcessing(true);

    try {
      const text = await readPdfText(uploadedFile);

      if (!text.trim()) {
        setError(errors.noText ?? 'Geen tekst gevonden in deze PDF. Is het een scan? Die kunnen we niet lezen.');
        setIsProcessing(false);
        return;
      }

      const invoice = extractInvoice(text);
      setExtracted(invoice);
      setResult(buildUbl(invoice, uploadedFile.name));
    } catch (err) {
      setError(errors.readFailed ?? 'We konden deze PDF niet lezen. Probeer een ander bestand.');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Reads the file in the browser: the invoice never leaves the visitor's
   * machine, which is what the privacy notice on this page promises.
   */
  const readPdfText = async (pdfFile: File): Promise<string> => {
    // The worker is served as a static file: bundling it through webpack
    // fails to parse, and a CDN copy would break the offline promise above.
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

    const doc = await pdfjs.getDocument({ data: await pdfFile.arrayBuffer() }).promise;
    const pages: string[] = [];

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber);
      const content = await page.getTextContent();
      // Group items into lines by their vertical position: labels and values
      // sit on the same line and the extractor reads them together.
      const lines = new Map<number, string[]>();
      for (const item of content.items as Array<{ str: string; transform: number[] }>) {
        if (!item.str) continue;
        const y = Math.round(item.transform[5]);
        const line = lines.get(y) ?? [];
        line.push(item.str);
        lines.set(y, line);
      }
      pages.push(
        [...lines.entries()]
          .sort((a, b) => b[0] - a[0])
          .map(([, parts]) => parts.join(' ').replace(/\s+/g, ' ').trim())
          .join('\n')
      );
    }

    return pages.join('\n');
  };

  /**
   * Builds UBL from what was actually read. Fields the PDF did not contain are
   * left as an obvious TE_CONTROLEREN marker rather than a plausible-looking
   * placeholder, so nobody files a document believing it is complete.
   */
  const buildUbl = (invoice: ExtractedInvoice, fileName: string) => {
    const todo = (label: string) => `TE_CONTROLEREN_${label}`;
    const money = (value: number | undefined) => (value === undefined ? '0.00' : value.toFixed(2));
    const escapeXml = (value: string) =>
      value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const issueDate = invoice.issueDate ?? new Date().toISOString().split('T')[0];
    const dueDate =
      invoice.dueDate ??
      new Date(new Date(issueDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const currency = invoice.currency;
    const taxPercent = invoice.taxPercent ?? 21;

    return `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
         xmlns:cac="urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2"
         xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
  <cbc:CustomizationID>urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0</cbc:CustomizationID>
  <cbc:ProfileID>urn:fdc:peppol.eu:2017:poacc:billing:01:1.0</cbc:ProfileID>
  <cbc:ID>${escapeXml(invoice.invoiceNumber ?? todo('FACTUURNUMMER'))}</cbc:ID>
  <cbc:IssueDate>${issueDate}</cbc:IssueDate>
  <cbc:DueDate>${dueDate}</cbc:DueDate>
  <cbc:InvoiceTypeCode>380</cbc:InvoiceTypeCode>
  <cbc:Note>${escapeXml(`Geconverteerd uit ${fileName}`)}</cbc:Note>
  <cbc:DocumentCurrencyCode>${currency}</cbc:DocumentCurrencyCode>

  <cac:AccountingSupplierParty>
    <cac:Party>
      <cac:PartyName>
        <cbc:Name>${escapeXml(invoice.supplierName ?? todo('LEVERANCIER'))}</cbc:Name>
      </cac:PartyName>${
        invoice.supplierVatNumber
          ? `
      <cac:PartyTaxScheme>
        <cbc:CompanyID>${escapeXml(invoice.supplierVatNumber)}</cbc:CompanyID>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:PartyTaxScheme>`
          : ''
      }
    </cac:Party>
  </cac:AccountingSupplierParty>

  <cac:AccountingCustomerParty>
    <cac:Party>
      <cac:PartyName>
        <cbc:Name>${todo('AFNEMER')}</cbc:Name>
      </cac:PartyName>
    </cac:Party>
  </cac:AccountingCustomerParty>

  <cac:TaxTotal>
    <cbc:TaxAmount currencyID="${currency}">${money(invoice.taxAmount)}</cbc:TaxAmount>
    <cac:TaxSubtotal>
      <cbc:TaxableAmount currencyID="${currency}">${money(invoice.taxExclusive)}</cbc:TaxableAmount>
      <cbc:TaxAmount currencyID="${currency}">${money(invoice.taxAmount)}</cbc:TaxAmount>
      <cac:TaxCategory>
        <cbc:ID>S</cbc:ID>
        <cbc:Percent>${taxPercent.toFixed(2)}</cbc:Percent>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:TaxCategory>
    </cac:TaxSubtotal>
  </cac:TaxTotal>

  <cac:LegalMonetaryTotal>
    <cbc:LineExtensionAmount currencyID="${currency}">${money(invoice.taxExclusive)}</cbc:LineExtensionAmount>
    <cbc:TaxExclusiveAmount currencyID="${currency}">${money(invoice.taxExclusive)}</cbc:TaxExclusiveAmount>
    <cbc:TaxInclusiveAmount currencyID="${currency}">${money(invoice.taxInclusive)}</cbc:TaxInclusiveAmount>
    <cbc:PayableAmount currencyID="${currency}">${money(invoice.taxInclusive)}</cbc:PayableAmount>
  </cac:LegalMonetaryTotal>

  <cac:InvoiceLine>
    <cbc:ID>1</cbc:ID>
    <cbc:InvoicedQuantity unitCode="C62">1</cbc:InvoicedQuantity>
    <cbc:LineExtensionAmount currencyID="${currency}">${money(invoice.taxExclusive)}</cbc:LineExtensionAmount>
    <cac:Item>
      <cbc:Name>${escapeXml(invoice.invoiceNumber ? `Factuur ${invoice.invoiceNumber}` : todo('OMSCHRIJVING'))}</cbc:Name>
      <cac:ClassifiedTaxCategory>
        <cbc:ID>S</cbc:ID>
        <cbc:Percent>${taxPercent.toFixed(2)}</cbc:Percent>
        <cac:TaxScheme><cbc:ID>VAT</cbc:ID></cac:TaxScheme>
      </cac:ClassifiedTaxCategory>
    </cac:Item>
    <cac:Price>
      <cbc:PriceAmount currencyID="${currency}">${money(invoice.taxExclusive)}</cbc:PriceAmount>
    </cac:Price>
  </cac:InvoiceLine>
</Invoice>`;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileUpload(droppedFile);
  };

  const downloadUbl = () => {
    if (!result) return;
    const blob = new Blob([result], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${Date.now()}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setExtracted(null);
    setError(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100">
          <FileOutput className="h-7 w-7 text-blue-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">
          {content.description}
        </p>
      </div>

      {/* Converter Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* File Upload */}
        <div
          className={`relative mb-4 rounded-xl border-2 border-dashed p-8 text-center transition-all ${
            isDragging ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          <Upload className="mx-auto h-10 w-10 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">{labels.dragDrop}</p>
          <p className="mt-1 text-xs text-slate-500">{labels.selectFile}</p>
          {file && (
            <p className="mt-3 flex items-center justify-center gap-2 text-sm text-brand-600">
              <FileText className="h-4 w-4" />
              {file.name}
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Processing */}
        {isProcessing && (
          <div className="mb-4 rounded-xl bg-brand-50 p-4 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600"></div>
            <p className="mt-2 text-sm font-medium text-brand-700">{labels.processing}</p>
          </div>
        )}

        {/* What we actually read out of the file - the previous version showed
            only XML, so a document full of placeholders looked like a result. */}
        {extracted && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-3 text-sm font-semibold text-slate-800">
              {labels.foundTitle ?? 'Uit je PDF gelezen'}
            </p>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {[
                [labels.fieldInvoiceNumber ?? 'Factuurnummer', extracted.invoiceNumber],
                [labels.fieldDate ?? 'Factuurdatum', extracted.issueDate],
                [labels.fieldSupplier ?? 'Leverancier', extracted.supplierName],
                [labels.fieldVatNumber ?? 'BTW-nummer', extracted.supplierVatNumber],
                [labels.fieldNet ?? 'Subtotaal', extracted.taxExclusive?.toFixed(2)],
                [labels.fieldVat ?? 'BTW', extracted.taxAmount?.toFixed(2)],
                [labels.fieldTotal ?? 'Totaal', extracted.taxInclusive?.toFixed(2)],
              ].map(([label, value]) => (
                <div key={label as string} className="flex justify-between gap-3">
                  <dt className="text-slate-600">{label}</dt>
                  <dd className={value ? 'font-medium text-slate-900' : 'text-amber-700'}>
                    {value || (labels.notFound ?? 'niet gevonden')}
                  </dd>
                </div>
              ))}
            </dl>
            {extracted.missing.length > 0 && (
              <p className="mt-3 flex items-start gap-2 text-xs text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {labels.checkBeforeUse ??
                  'Wat we niet konden lezen staat in de XML als TE_CONTROLEREN. Vul dat aan voordat je de factuur verstuurt.'}
              </p>
            )}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-slate-700">{labels.preview}</label>
            <pre className="max-h-64 overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
              {result}
            </pre>
            <button
              type="button"
              onClick={downloadUbl}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-700"
            >
              <Download className="h-4 w-4" />
              {labels.download}
            </button>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset}
        </button>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            {content.disclaimer.heading}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            {content.disclaimer.content}
          </p>
        </div>
      )}
    </div>
  );
}
